import { useEffect, useMemo, useRef, useState } from "react";
import { ArrowLeft, ArrowRight, CheckCircle2, MessageCircle, Send } from "lucide-react";
import { quizQuestions } from "../data/siteContent";
import { submitQuizLead } from "../lib/api";
import { whatsappUrl } from "../lib/whatsapp";

const slugify = (text) => text.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

function getRecommendation(answers) {
  const values = Object.values(answers).join(" ").toLowerCase();
  const primaryNeed = (answers.need || "").toLowerCase();
  const confidence = (answers.confidence || "").toLowerCase();
  if (values.includes("first job") || values.includes("helping my child")) {
    return { service: "Young Person Starter Support", explanation: "This is best suited to first jobs, apprenticeships, early career applications or support for a young person who needs clearer next steps." };
  }
  if (primaryNeed.includes("not sure") || confidence.includes("stuck")) {
    return { service: "General Career Support Check-In", explanation: "A short check-in can help identify whether your CV, applications, LinkedIn profile or interview confidence needs the most attention." };
  }
  if (values.includes("interview")) {
    return { service: "Interview Preparation Session", explanation: "This will help you practise likely questions, structure answers and build confidence before the interview." };
  }
  if (values.includes("cover letter") || values.includes("applications")) {
    return { service: "CV + Cover Letter or Job Application Support", explanation: "This route helps align your CV, cover letter or application answers with the role you want." };
  }
  if (values.includes("cv") || values.includes("tailoring") || values.includes("created") || values.includes("hearing back")) {
    return { service: "CV Review or CV Rewrite", explanation: "This will help make your CV clearer, stronger and better matched to the jobs you are applying for." };
  }
  return { service: "General Career Support Check-In", explanation: "Ben can look at your situation and help you choose the most useful next step." };
}

export default function Quiz() {
  const quizTopRef = useRef(null);
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [status, setStatus] = useState({ type: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const isComplete = step >= quizQuestions.length;
  const currentQuestion = quizQuestions[step];
  const recommendation = useMemo(() => getRecommendation(answers), [answers]);
  const progress = isComplete ? 100 : ((step + 1) / quizQuestions.length) * 100;
  const answerList = quizQuestions.map((question) => ({ question: question.question, answer: answers[question.id] || "" }));
  const quizWhatsAppMessage = `Hi Ben, I completed the Job Boost by Ben quiz. Recommended service: ${recommendation.service}. My answers: ${answerList.map((item) => `${item.question} ${item.answer}`).join(" | ")}`;

  useEffect(() => {
    if (step > 0) {
      quizTopRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [step]);

  const selectAnswer = (option) => {
    setAnswers((previous) => ({ ...previous, [currentQuestion.id]: option }));
  };

  const nextStep = () => {
    if (step < quizQuestions.length - 1) setStep((value) => value + 1);
  };

  const submitLead = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    setStatus({ type: "", message: "" });
    try {
      const payload = { ...form, answers: answerList, recommended_service: recommendation.service };
      const response = await submitQuizLead(payload);
      setStatus({ type: "success", message: `Thanks ${response.name}. Your quiz result has been saved for Ben.` });
    } catch (error) {
      setStatus({ type: "error", message: "Sorry, the quiz result could not be sent. Please check your details or message Ben on WhatsApp." });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <section className="page-hero" data-testid="quiz-hero-section">
        <div className="container">
          <span className="eyebrow" data-testid="quiz-eyebrow">Free Career Support Quiz</span>
          <h1 className="page-title" data-testid="quiz-page-title">Free Career Support Quiz</h1>
          <p className="page-intro" data-testid="quiz-intro-text">Not sure what help you need? Answer a few quick questions and get a simple recommendation.</p>
        </div>
      </section>
      <section className="section compact" data-testid="quiz-tool-section" ref={quizTopRef}>
        <div className="container quiz-layout">
          <aside className="quiz-panel" data-testid="quiz-progress-panel">
            <h2 data-testid="quiz-progress-title">Your progress</h2>
            <div className="progress-track" data-testid="quiz-progress-track"><div className="progress-fill" style={{ width: `${progress}%` }} data-testid="quiz-progress-fill" /></div>
            <p className="body-copy" data-testid="quiz-progress-text">{isComplete ? "Recommendation ready" : `Question ${step + 1} of ${quizQuestions.length}`}</p>
          </aside>

          {!isComplete ? (
            <div className="quiz-panel" data-testid="quiz-question-panel">
              <h2 className="question-title" data-testid={`quiz-question-${step + 1}-title`}>{currentQuestion.question}</h2>
              <div className="option-grid" data-testid={`quiz-question-${step + 1}-options`}>
                {currentQuestion.options.map((option) => (
                  <button key={option} type="button" className={`btn option-button ${answers[currentQuestion.id] === option ? "selected" : ""}`} onClick={() => selectAnswer(option)} data-testid={`quiz-option-${currentQuestion.id}-${slugify(option)}-button`}>
                    <CheckCircle2 size={19} /> {option}
                  </button>
                ))}
              </div>
              <div className="quiz-controls">
                <button className="btn btn-secondary" type="button" onClick={() => setStep((value) => Math.max(0, value - 1))} disabled={step === 0} data-testid="quiz-back-button"><ArrowLeft size={18} /> Back</button>
                {step === quizQuestions.length - 1 ? (
                  <button className="btn btn-primary" type="button" onClick={() => setStep(quizQuestions.length)} disabled={!answers[currentQuestion.id]} data-testid="quiz-see-result-button">See my result <ArrowRight size={18} /></button>
                ) : (
                  <button className="btn btn-primary" type="button" onClick={nextStep} disabled={!answers[currentQuestion.id]} data-testid="quiz-next-button">Next question <ArrowRight size={18} /></button>
                )}
              </div>
            </div>
          ) : (
            <div className="quiz-panel" data-testid="quiz-result-panel">
              <div className="result-box" data-testid="quiz-result-box">
                <span className="eyebrow" data-testid="quiz-result-eyebrow">Recommended service</span>
                <h2 data-testid="quiz-recommended-service">{recommendation.service}</h2>
                <p className="body-copy" data-testid="quiz-recommendation-explanation">{recommendation.explanation}</p>
              </div>
              <form className="form-grid" onSubmit={submitLead} data-testid="quiz-lead-form">
                <div className="form-row">
                  <div className="field"><label htmlFor="quiz-name" data-testid="quiz-name-label">Name</label><input id="quiz-name" value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} required data-testid="quiz-name-input" /></div>
                  <div className="field"><label htmlFor="quiz-email" data-testid="quiz-email-label">Email</label><input id="quiz-email" type="email" value={form.email} onChange={(event) => setForm({ ...form, email: event.target.value })} required data-testid="quiz-email-input" /></div>
                </div>
                <div className="field"><label htmlFor="quiz-phone" data-testid="quiz-phone-label">Phone number optional</label><input id="quiz-phone" value={form.phone} onChange={(event) => setForm({ ...form, phone: event.target.value })} data-testid="quiz-phone-input" /></div>
                <div className="field"><label htmlFor="quiz-message" data-testid="quiz-message-label">Message optional</label><textarea id="quiz-message" value={form.message} onChange={(event) => setForm({ ...form, message: event.target.value })} data-testid="quiz-message-input" /></div>
                {status.message && <div className={`form-status ${status.type}`} data-testid="quiz-form-status-message">{status.message}</div>}
                <div className="hero-actions">
                  <button className="btn btn-primary" type="submit" disabled={submitting} data-testid="quiz-send-result-button"><Send size={18} /> {submitting ? "Sending..." : "Send My Quiz Result to Ben"}</button>
                  <a className="btn btn-secondary" href={whatsappUrl(quizWhatsAppMessage)} target="_blank" rel="noopener noreferrer" data-testid="quiz-message-whatsapp-button"><MessageCircle size={18} /> Message Ben on WhatsApp</a>
                </div>
              </form>
            </div>
          )}
        </div>
      </section>
    </>
  );
}